import classNames from "classnames";
import * as React from "react";
import { StyleClass } from "source/app/presentation/Shared/Styles/CSS";

const baseButtonClass = StyleClass("touchActionButtonBase", {
    border: "none",
    margin: 0,
    padding: 0,
    background: "transparent",
    color: "inherit",
    font: "inherit",
    cursor: "pointer",
    touchAction: "manipulation",
    appearance: "none",
});

const followUpMouseEventTypes = ["mousedown", "mouseup", "click"] as const;
const followUpMouseEventWindowInMilliseconds = 750;
const followUpMouseEventCoordinateToleranceInPixels = 2;

function BlockFollowUpMouseEvents(
    event: React.PointerEvent<HTMLButtonElement>
) {
    const x = event.clientX;
    const y = event.clientY;
    let clearTimeoutId: number | undefined;

    const cleanup = () => {
        window.clearTimeout(clearTimeoutId);
        followUpMouseEventTypes.forEach((eventType) =>
            window.removeEventListener(eventType, blockFollowUpMouseEvent, true)
        );
    };

    const blockFollowUpMouseEvent = (followUpEvent: MouseEvent) => {
        const isAtActivationPoint =
            Math.abs(followUpEvent.clientX - x) <=
                followUpMouseEventCoordinateToleranceInPixels &&
            Math.abs(followUpEvent.clientY - y) <=
                followUpMouseEventCoordinateToleranceInPixels;

        if (!followUpEvent.isTrusted || !isAtActivationPoint) {
            return;
        }

        followUpEvent.preventDefault();
        followUpEvent.stopImmediatePropagation();

        if (followUpEvent.type === "click") {
            cleanup();
        }
    };

    followUpMouseEventTypes.forEach((eventType) =>
        window.addEventListener(eventType, blockFollowUpMouseEvent, true)
    );
    clearTimeoutId = window.setTimeout(
        cleanup,
        followUpMouseEventWindowInMilliseconds
    );
}

export function TouchActionButton({
    children,
    className,
    onActivate,
    ariaLabel,
    title,
    disabled = false,
    activateOnPointerUp = false,
}: {
    children: React.ReactNode;
    className?: string;
    onActivate: () => void;
    ariaLabel?: string;
    title?: string;
    disabled?: boolean;
    activateOnPointerUp?: boolean;
}) {
    const lastPointerActivationAt = React.useRef(0);

    const handleClick = () => {
        if (disabled) {
            return;
        }

        if (
            Date.now() - lastPointerActivationAt.current <
            followUpMouseEventWindowInMilliseconds
        ) {
            return;
        }

        onActivate();
    };

    const handlePointerUp = (event: React.PointerEvent<HTMLButtonElement>) => {
        if (disabled || !activateOnPointerUp) {
            return;
        }

        BlockFollowUpMouseEvents(event);
        event.preventDefault();
        lastPointerActivationAt.current = Date.now();
        onActivate();
    };

    return (
        <button
            type="button"
            aria-label={ariaLabel}
            title={title}
            className={classNames(baseButtonClass, className)}
            onClick={handleClick}
            onPointerUp={handlePointerUp}
            disabled={disabled}
        >
            {children}
        </button>
    );
}
